import React from 'react'
import styled from '@emotion/styled'
import {Spacing} from 'types/Theme'

import {Button} from '@vio/atlas/Button'
import {Divider} from '@vio/atlas/Divider'
import {NoStyleButton} from '@vio/atlas/helpers/NoStyleButton'
import {Icon} from '@vio/atlas/Icon'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {FilterDisplayContainerProps} from './FilterDisplayContainer'

const Flex = styled.div`
  display: flex;
  justify-content: space-between;
  align-content: center;
`

const PopoverHeader = styled.div`
  padding: ${cssTheme.layout.spacing.s600};
`

const Nothing: React.FC = ({children}) => <>{children}</>

const OverlayHeader = styled.div`
  position: relative;
  padding: ${cssTheme.layout.spacing.s500} ${cssTheme.layout.spacing.s600};
  text-align: center;
  border-bottom: 1px solid ${cssTheme.colors.border.neutral.secondary};
`

const Subtitle = styled(Text)`
  margin-top: ${cssTheme.layout.spacing.s200};
  text-transform: lowercase;
  &::first-letter {
    text-transform: capitalize;
  }
`

const PopoverTitle = styled.span`
  ${cssTheme.typography.labelM};
  margin-block: 0;
  margin-inline: 0 ${cssTheme.layout.spacing.s600};
`

const OverlayTitle = styled.span`
  ${cssTheme.typography.labelM};
`

const ChildrenWrapper = styled.div<{
  hasHorizontalPadding: boolean
  paddingBottom: Spacing
}>`
  padding-left: ${({hasHorizontalPadding}) =>
    hasHorizontalPadding ? cssTheme.layout.spacing.s600 : 0};
  padding-right: ${({hasHorizontalPadding}) =>
    hasHorizontalPadding ? cssTheme.layout.spacing.s600 : 0};
  padding-bottom: ${({paddingBottom}) =>
    cssTheme.layout.spacing[paddingBottom]};
`

const Footer = styled.div`
  padding: ${cssTheme.layout.spacing.s500} ${cssTheme.layout.spacing.s600};
  display: flex;
  justify-content: space-between;
  z-index: 3;
  background: ${cssTheme.colors.background.neutral.primary};
  position: relative;
`

const PopoverCloseButton = styled(NoStyleButton)`
  color: ${cssTheme.colors.content.neutral.secondary};

  &:focus-visible {
    outline: 2px solid ${cssTheme.colors.border.neutral.primaryHover};
    outline-offset: 2px;
    border-radius: 4px;
  }
`

const OverlayCloseButton = styled(NoStyleButton)`
  color: ${cssTheme.colors.content.neutral.secondary};
  height: 24px;
  position: absolute;
  right: ${cssTheme.layout.spacing.s600};
`

type FilterDisplayContentProps = Pick<
  FilterDisplayContainerProps,
  | 'title'
  | 'subtitle'
  | 'children'
  | 'onClose'
  | 'onClear'
  | 'onApply'
  | 'type'
  | 'paddingBottom'
  | 'dataId'
>

export const FilterDisplayContent: React.FC<FilterDisplayContentProps> = ({
  title,
  dataId,
  subtitle,
  children,
  onClose,
  onClear,
  onApply,
  type,
  paddingBottom
}) => {
  const Header = type === 'overlay' ? OverlayHeader : PopoverHeader
  const Title = type === 'overlay' ? OverlayTitle : PopoverTitle
  const TitleWrapper = type === 'overlay' ? Nothing : Flex
  const CloseButton =
    type === 'overlay' ? OverlayCloseButton : PopoverCloseButton

  return (
    <div>
      <Header>
        <TitleWrapper>
          <Title>{title}</Title>
          <CloseButton onClick={onClose}>
            <Icon name="Close" />
          </CloseButton>
        </TitleWrapper>

        {subtitle && (
          <Subtitle variant="bodyS" colorPath="content.neutral.secondary">
            {subtitle}
          </Subtitle>
        )}
      </Header>
      <ChildrenWrapper
        hasHorizontalPadding={type === 'popover'}
        paddingBottom={paddingBottom}
      >
        {children}
      </ChildrenWrapper>
      <Divider />
      <Footer data-id={`${dataId}Buttons`}>
        <Button
          dataId="clearFiltersButton"
          onClick={onClear}
          variant="tertiary"
        >
          <FormattedMessageWrapper id="clear" defaultMessage="Clear" />
        </Button>
        <Button dataId="applyFiltersButton" onClick={onApply}>
          <FormattedMessageWrapper id="apply" defaultMessage="Apply" />
        </Button>
      </Footer>
    </div>
  )
}
